import { TFunction } from 'i18next';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import {
    FiCardType,
    hasFinnishPaymentFields,
    hasDanishPaymentFields,
    hasNorwegianPaymentFields,
    hasSwedishGiroFields,
    isValidBankgiro,
    isValidBic,
    isValidDanishAccountNumber,
    isValidDanishRegistrationNumber,
    isValidFiCreditorNumber,
    isValidIban,
    isValidNorwegianAccountNumber,
    isValidPlusgiro,
    KID_MAX_LENGTH,
    KID_MIN_LENGTH,
    KidCheckDigitMethod,
    PaymentBoxAccount,
} from 'utils/payment-details-validation';

type FormErrors = Record<string, string>;

// DD-3700: a Norwegian lab's account number and KID agreement.
const validateNorwegianPaymentDetails = (formData: InvoiceDetailsSettingsFormData, translate: TFunction) => {
    const errors: FormErrors = {};
    const accountNumber = formData.accountNumber.trim();
    if (accountNumber && !isValidNorwegianAccountNumber(accountNumber)) {
        errors.accountNumber = translate('lab-settings:INVALID_ACCOUNT_NUMBER');
    }
    if (!formData.kidEnabled) {
        return errors;
    }
    if (!accountNumber) {
        errors.accountNumber = translate('lab-settings:KID_REQUIRES_ACCOUNT_NUMBER');
    }
    const kidLength = Number(formData.kidLength);
    if (!Number.isInteger(kidLength) || kidLength < KID_MIN_LENGTH || kidLength > KID_MAX_LENGTH) {
        errors.kidLength = translate('lab-settings:KID_LENGTH_INVALID');
    }
    if (!Object.values(KidCheckDigitMethod).includes(formData.kidCheckDigitMethod as KidCheckDigitMethod)) {
        errors.kidCheckDigitMethod = translate('lab-settings:KID_CHECK_DIGIT_METHOD_REQUIRED');
    }
    return errors;
};

// DD-3700: a Danish lab's reg.nr. + account number and FI agreement.
const validateDanishPaymentDetails = (formData: InvoiceDetailsSettingsFormData, translate: TFunction) => {
    const errors: FormErrors = {};
    const registrationNumber = formData.registrationNumber.trim();
    const accountNumber = formData.accountNumber.trim();
    if (registrationNumber && !isValidDanishRegistrationNumber(registrationNumber)) {
        errors.registrationNumber = translate('lab-settings:INVALID_REGISTRATION_NUMBER');
    } else if (!registrationNumber && accountNumber) {
        errors.registrationNumber = translate('lab-settings:REGISTRATION_AND_ACCOUNT_REQUIRED');
    }
    if (accountNumber && !isValidDanishAccountNumber(accountNumber)) {
        errors.accountNumber = translate('lab-settings:INVALID_DANISH_ACCOUNT_NUMBER');
    } else if (registrationNumber && !accountNumber) {
        errors.accountNumber = translate('lab-settings:REGISTRATION_AND_ACCOUNT_REQUIRED');
    }
    if (!formData.fiEnabled) {
        return errors;
    }
    if (!Object.values(FiCardType).includes(formData.fiCardType as FiCardType)) {
        errors.fiCardType = translate('lab-settings:FI_CARD_TYPE_REQUIRED');
    }
    if (!isValidFiCreditorNumber(formData.fiCreditorNumber.trim())) {
        errors.fiCreditorNumber = translate('lab-settings:INVALID_FI_CREDITOR_NUMBER');
    }
    return errors;
};

// DD-3700: a Finnish lab's late payment interest (0-100 %) and complaint period (0-365 days).
/** Formik stores a number input's value as a number once typed in; read it either way. */
export const numberFieldText = (value: string | number | undefined | null) => String(value ?? '').trim();

const validateFinnishInvoiceTerms = (formData: InvoiceDetailsSettingsFormData, translate: TFunction) => {
    const errors: FormErrors = {};
    const interest = numberFieldText(formData.latePaymentInterest);
    if (interest && !(Number(interest) >= 0 && Number(interest) <= 100)) {
        errors.latePaymentInterest = translate('lab-settings:LATE_PAYMENT_INTEREST_INVALID');
    }
    const days = numberFieldText(formData.complaintPeriodDays);
    if (days && !(Number.isInteger(Number(days)) && Number(days) >= 0 && Number(days) <= 365)) {
        errors.complaintPeriodDays = translate('lab-settings:COMPLAINT_PERIOD_INVALID');
    }
    return errors;
};

const validateSwedishGiro = (formData: InvoiceDetailsSettingsFormData, translate: TFunction) => {
    const errors: FormErrors = {};
    const bankgiro = formData.bankgiro.trim();
    const postgiro = formData.postgiro.trim();
    if (bankgiro && !isValidBankgiro(bankgiro)) {
        errors.bankgiro = translate('lab-settings:INVALID_BANKGIRO');
    }
    if (postgiro && !isValidPlusgiro(postgiro)) {
        errors.postgiro = translate('lab-settings:INVALID_PLUSGIRO');
    }
    if (
        (formData.paymentBoxAccount === PaymentBoxAccount.BANKGIRO && !bankgiro) ||
        (formData.paymentBoxAccount === PaymentBoxAccount.PLUSGIRO && !postgiro)
    ) {
        errors.paymentBoxAccount = translate('lab-settings:PAYMENT_BOX_ACCOUNT_MISSING');
    }
    return errors;
};

const validateIbanAndBic = (iban: string, bicSwift: string, translate: TFunction) => {
    const errors: FormErrors = {};
    if (iban && !isValidIban(iban)) {
        errors.iban = translate('lab-settings:INVALID_IBAN');
    } else if (!iban && bicSwift) {
        errors.iban = translate('organization:VALIDATION:BIC_SWIFT_IBAN_REQUIRED');
    }
    if (bicSwift && !isValidBic(bicSwift)) {
        errors.bicSwift = translate('lab-settings:INVALID_BIC');
    } else if (iban && !bicSwift) {
        errors.bicSwift = translate('organization:VALIDATION:BIC_SWIFT_IBAN_REQUIRED');
    }
    return errors;
};

/**
 * DD-3700: format checks for the "Receive payments" fields of the lab's country, so the lab sees
 * a mistake while typing. The backend repeats them (shared-denthub-code invoice-payment.ts).
 */
export const validatePaymentDetailsForm = (
    formData: InvoiceDetailsSettingsFormData,
    labCountry: string | undefined,
    translate: TFunction,
): FormErrors => {
    const isSwedishLab = hasSwedishGiroFields(labCountry);
    const isNorwegianLab = hasNorwegianPaymentFields(labCountry);
    const isDanishLab = hasDanishPaymentFields(labCountry);
    const iban = formData.iban.trim();
    const bicSwift = formData.bicSwift.trim();
    const errors: FormErrors = {
        ...(isSwedishLab ? validateSwedishGiro(formData, translate) : {}),
        ...(isNorwegianLab ? validateNorwegianPaymentDetails(formData, translate) : {}),
        ...(isDanishLab ? validateDanishPaymentDetails(formData, translate) : {}),
        ...(hasFinnishPaymentFields(labCountry) ? validateFinnishInvoiceTerms(formData, translate) : {}),
        ...validateIbanAndBic(iban, bicSwift, translate),
    };
    const hasDomesticAccount =
        (isSwedishLab && Boolean(formData.bankgiro.trim() || formData.postgiro.trim())) ||
        ((isNorwegianLab || isDanishLab) && Boolean(formData.accountNumber.trim())) ||
        (isDanishLab && formData.fiEnabled);
    if (!hasDomesticAccount && !iban && !bicSwift) {
        const firstField =
            (isSwedishLab && 'bankgiro') ||
            (isDanishLab && 'registrationNumber') ||
            (isNorwegianLab && 'accountNumber') ||
            'iban';
        errors[firstField] = translate('organization:VALIDATION:PAYMENT_DETAILS_NOT_SET');
    }
    return errors;
};
